<template>
  <!-- TKP-219: "Kaynak Verileri" — çalışma alanının kaynak–veri eşleşmeleri (eski Şablon Yönetimi özelliği; o ekran kapalı kalır) -->
  <div data-test="source-data">
    <!-- TKP-235 m.185: açıklama kalktı -->
    <h6 class="mt-2">{{ $t("process-data.source-data.pick-source") }}</h6>
    <filter-select id="sourceDataDatasourceFilter" ref="sourceDataDatasourceFilter" :options-limit="LIMIT" :placeholder="$t('datasource.text')" :multiple="false" @filter="onDatasourceSearch" :selected="datasource" @changed="onDatasource" />
    <new-entry-link :label="$t('process-data.create-new.link.datasource')" :placeholder="$t('process-data.create-new.name')" data-test="source-data-new-datasource" @add="(q) => onDatasource(newEntry(q, label('datasource')))" />
    <template v-if="datasource">
      <content-loading v-if="loading" />
      <template v-else>
        <p v-if="!canEdit" class="small text-info mt-2 mb-1" data-test="source-data-readonly"><i class="bx bx-lock-alt"></i> {{ $t("process-data.source-data.read-only") }}</p>
        <!-- TKP-236 n.8: ekleme ve silme birikir, "Kaydet" ikisini birlikte yazar (silince de etkin); düğme sağ altta -->
        <h6 class="mt-3">{{ $t("process-data.source-data.list", { count: visibleRows.length }) }}</h6>
        <div v-if="!visibleRows.length" class="text-muted small" data-test="source-data-empty">{{ $t("process-data.source-data.empty") }}</div>
        <div v-for="r in visibleRows" :key="r.key" class="d-flex align-items-center small py-1 border-bottom" :data-test="'source-data-row-' + r.key">
          <span class="flex-grow-1" :class="{ 'text-muted': r.removed }" :style="r.removed ? 'text-decoration: line-through' : ''">
            {{ r.name | translate }}
            <b-badge v-if="r.added" variant="info" class="ml-1" data-test="source-data-pending-add">{{ $t("process-data.from-datasource.new-badge") }}</b-badge>
          </span>
          <template v-if="canEdit">
            <i v-if="r.removed" class="bx bx-undo text-primary hand" data-test="source-data-undo" v-b-tooltip.hover :title="$t('common.cancel')" @click="undoRemove(r)"></i>
            <i v-else-if="r.added || r.removable" class="bx bx-trash-alt text-danger hand" data-test="source-data-remove" v-b-tooltip.hover :title="$t('common.delete')" @click="remove(r)"></i>
          </template>
        </div>
        <template v-if="canEdit">
          <h6 class="mt-3">{{ $t("process-data.source-data.add") }}</h6>
          <filter-select id="sourceDataDataFilter" ref="sourceDataDataFilter" :options-limit="200" :placeholder="$t('data.select')" :multiple="false" @filter="onDataSearch" :selected="data" @changed="onDataPick" />
          <!-- TKP-229: yeni veri eski veri ekleme ekranıyla (ad + kategori zorunlu) açılır; ayrı kategori kutusu kalktı -->
          <span class="text-primary hand d-inline-block mt-1" data-test="source-data-new-data-open" @click="$refs.newData.open()">{{ $t("process-data.create-new.link.data") }}</span>
          <div class="d-flex justify-content-end mt-4">
            <sweet-button :loading="saving" :disabled="saving || !dirty" variant="success" icon="mdi mdi-check" size="sm" data-test="source-data-add" @click="save()">
              {{ $t("process-data.source-data.add-button") }}
            </sweet-button>
          </div>
        </template>
      </template>
    </template>
    <new-data-dialog ref="newData" :process="processRef()" @created="onNewData" />
  </div>
</template>

<script>
import rest from "@/rest-client";
import NewEntryLink from "./new-entry-link";
import NewDataDialog from "./new-data-dialog"; // TKP-229
import { cachedPicker, isLibraryPick, isNewEntry, mergeOrgAndLibrary, newEntry, pendingFirst, searchRequest, withCreateOption } from "../unified-picker";
import { createDictionaryEntry } from "../new-dictionary-entry";

const LIMIT = 10000;
/** Seçilen kaynak / verinin sunucuya gidecek kimliği: kütüphane kalemi ise `fromLibrary`. */
export const refOf = (item) => (item && item._id ? { _id: String(item._id), ...(isLibraryPick(item) ? { fromLibrary: true } : {}) } : null);

/**
 * TKP-219 (kullanıcı kararları 2026-10-15): kaynak seçilir, çalışma alanında görünen verileri listelenir (Tunesoft / çalışma alanı).
 * Çalışma alanı yöneticisi ya da organizasyon sahibi / onay yetkilisi veri ekler (çalışma alanı eşleşmesi) ve çalışma alanının
 * eklediğini kaldırır; Tunesoft eşleşmesi salt okunur; yetkisiz kullanıcı salt okunur görür (yetki sunucuda). Listede olmayan kaynak /
 * veri önce organizasyon sözlüğünde açılır (her zamanki yol); sunucu kütüphanede yoksa çalışma alanı kütüphanesine ekler.
 */
export default {
  components: { NewEntryLink, NewDataDialog },
  // TKP-221: "Veri seç" sekmesinde seçili kaynaklar (yeni girilenler dahil) seçicinin başında
  props: { process: { type: Object, default: null }, pendingSources: { type: Array, default: () => [] } },
  data() {
    return { LIMIT, datasource: null, ref: null, loading: false, canEdit: false, pairs: [], data: null, saving: false, sourcePicker: null, dataPicker: null, pendingAdds: [], pendingRemoves: [] };
  },
  computed: {
    /** Liste: kayıtlı eşleşmeler (silinecekse üstü çizili) + bekleyen eklemeler. */
    visibleRows() {
      const removed = new Set(this.pendingRemoves);
      const saved = this.pairs.map((p) => ({ key: p._id, pairId: p._id, name: p.data.name, removable: p.removable, removed: removed.has(p._id) }));
      const added = this.pendingAdds.map((d) => ({ key: "new-" + d._id, doc: d, name: d.name, added: true }));
      return [...saved, ...added];
    },
    dirty() {
      return this.pendingAdds.length > 0 || this.pendingRemoves.length > 0;
    },
  },
  watch: {
    /** TKP-221: filter-select seçenekleri yalnız yüklenirken bir kez çekiyor — "Veri seç"te kaynak eklenince/çıkarılınca liste yenilensin. */
    pendingSources() {
      this.refreshSources();
    },
  },
  methods: {
    newEntry,
    refreshSources() {
      const select = this.$refs.sourceDataDatasourceFilter;
      if (select && select.searchChanged) select.searchChanged("*");
    },
    label(kind) {
      return (q) => this.$t(`process-data.create-new.${kind}`, { name: q });
    },
    processRef() {
      return this.process && !this.process.__draft && this.process._id ? this.process : undefined;
    },
    async onDatasourceSearch(value, callback) {
      if (!this.sourcePicker) {
        this.sourcePicker = cachedPicker(async (v) => {
          const request = searchRequest(v, this.currentLanguage, LIMIT);
          const [org, library] = await Promise.all([
            this.$store.dispatch("organizationDatasource/paginateForSearch", request),
            this.$store.dispatch("datasource/paginateForSearch", request).catch(() => ({ data: { docs: [] } })),
          ]);
          const lang = this.currentLanguage || "tr";
          const text = (d) => String((d && d.name && (d.name[lang] || d.name.tr || d.name.en)) || "").trim();
          return mergeOrgAndLibrary(org.data?.docs ?? [], library.data?.docs ?? []).sort((a, b) => text(a).localeCompare(text(b), lang));
        }, LIMIT);
      }
      callback(withCreateOption(pendingFirst(await this.sourcePicker(value), this.pendingSources, value), value, this.label("datasource")));
    },
    async onDataSearch(value, callback) {
      if (!this.dataPicker) {
        this.dataPicker = cachedPicker(async (v) => {
          const request = searchRequest(v, this.currentLanguage);
          const [org, library] = await Promise.all([
            this.$store.dispatch("organizationData/paginateForSearch", request),
            this.$store.dispatch("data/paginateForSearch", request).catch(() => ({ data: { docs: [] } })),
          ]);
          return mergeOrgAndLibrary(org.data?.docs ?? [], library.data?.docs ?? []);
        });
      }
      callback(withCreateOption(await this.dataPicker(value), value, this.label("data")));
    },
    /** TKP-229: aramadaki "➕ Yeni veri oluştur" seçimi eski veri ekleme ekranını adıyla açar. */
    onDataPick(v) {
      if (isNewEntry(v)) {
        this.data = null;
        this.$refs.newData.open(v.__new);
        return;
      }
      this.stage(v && v._id ? v : null);
    },
    onNewData(doc) {
      this.dataPicker = null; // açılan veri listede görünsün
      this.stage(doc);
      this.$emit("data-created", doc); // TKP-230: "Veri seç" / "Veri Kaynağı" veri listeleri de yenilensin
    },
    async onDatasource(item) {
      this.datasource = item && item._id ? item : null;
      this.pairs = [];
      this.ref = null;
      if (!this.datasource) return;
      try {
        // listede olmayan kaynak: organizasyon sözlüğünde açılır (her zamanki yol)
        const picked = this.datasource;
        const doc = isNewEntry(picked) ? await createDictionaryEntry("/datasource", picked.__new, { process: this.processRef(), language: this.currentLanguage }) : picked;
        if (isNewEntry(picked)) {
          this.datasource = doc;
          this.sourcePicker = null; // açılan kaynak listede görünsün
          this.$nextTick(() => this.refreshSources());
          this.$emit("created", { from: picked, doc }); // TKP-221: "Veri seç" geçici girdiyi bununla değiştirir (ikinci kez açılmaz)
        }
        this.ref = refOf(doc);
        await this.load();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async load() {
      if (!this.ref) return;
      this.loading = true;
      try {
        const { data } = await rest.post("/quick-hierarchy/source-data/list", { datasource: this.ref });
        this.canEdit = !!data.canEdit;
        this.pairs = data.pairs || [];
        this.pendingAdds = [];
        this.pendingRemoves = [];
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.loading = false;
      }
    },
    /** TKP-236 n.8: seçilen veri listeye "yeni" olarak eklenir (kaydedilmez); kayıtlı ya da zaten eklenmiş veri ikinci kez girmez. */
    stage(doc) {
      this.data = null;
      if (!doc || !doc._id) return;
      const same = (a, b) => String(a) === String(b);
      const libraryId = doc.fromLibrary ? doc._id : doc.libraryItem && (doc.libraryItem._id || doc.libraryItem);
      const taken = this.pairs.some((p) => !this.pendingRemoves.includes(p._id) && (same(p.data._id, doc._id) || (libraryId && same(p.data._id, libraryId)))) || this.pendingAdds.some((d) => same(d._id, doc._id));
      if (taken) return this.$toast.info(this.$t("process-data.source-data.already"));
      this.pendingAdds = [...this.pendingAdds, doc];
    },
    remove(row) {
      if (row.added) this.pendingAdds = this.pendingAdds.filter((d) => String(d._id) !== String(row.doc._id));
      else this.pendingRemoves = [...this.pendingRemoves, row.pairId];
    },
    undoRemove(row) {
      this.pendingRemoves = this.pendingRemoves.filter((id) => id !== row.pairId);
    },
    /** Bekleyen silme ve eklemeleri yazar; hata olan kalem bildirilir, ötekiler yazılır. */
    async save() {
      if (!this.ref || !this.dirty || !(await this.confirm(this.$t("alert.approve")))) return;
      this.saving = true;
      let failed = 0;
      try {
        for (const id of this.pendingRemoves) {
          try {
            await rest.delete(`/quick-hierarchy/source-data/${id}`);
          } catch (error) {
            failed++;
            this.$handleHttpErrorByStatusCode(error);
          }
        }
        for (const doc of this.pendingAdds) {
          try {
            await rest.post("/quick-hierarchy/source-data", { datasource: this.ref, data: refOf(doc) });
          } catch (error) {
            failed++;
            this.$handleHttpErrorByStatusCode(error);
          }
        }
        if (!failed) this.$toast.success(this.$t("process-data.source-data.saved"));
        await this.load();
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
